'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const deals = [
  {
    id: 'deal-001',
    type: 'flight',
    origin: 'New York (JFK)',
    destination: 'London (LHR)',
    airline: 'British Airways',
    airlineCode: 'BA',
    price: 389,
    originalPrice: 612,
    savings: 223,
    departure: 'Jun 14',
    duration: '7h 15m',
    stops: 'Nonstop',
    badge: 'Flash Deal',
    badgeColor: 'price-badge-popular',
    priceTrend: 'down',
    percentOff: 36,
  },
  {
    id: 'deal-002',
    type: 'hotel',
    destination: 'Paris, France',
    hotel: 'Hôtel Régence Étoile',
    stars: 4,
    pricePerNight: 118,
    originalPrice: 195,
    savings: 77,
    nights: 5,
    rating: 8.7,
    reviewCount: 1243,
    badge: 'Best Value',
    badgeColor: 'price-badge-best',
    priceTrend: 'down',
    percentOff: 39,
  },
  {
    id: 'deal-003',
    type: 'package',
    origin: 'Los Angeles (LAX)',
    destination: 'Cancún',
    hotel: 'Fiesta Americana Grand',
    price: 799,
    originalPrice: 1340,
    savings: 541,
    nights: 7,
    departure: 'Jul 4',
    badge: 'Package Deal',
    badgeColor: 'price-badge-cheapest',
    priceTrend: 'down',
    percentOff: 40,
  },
  {
    id: 'deal-004',
    type: 'flight',
    origin: 'Chicago (ORD)',
    destination: 'Tokyo (NRT)',
    airline: 'ANA',
    airlineCode: 'NH',
    price: 498,
    originalPrice: 890,
    savings: 392,
    departure: 'Aug 2',
    duration: '13h 40m',
    stops: '1 stop',
    badge: 'Limited Seats',
    badgeColor: 'price-badge-popular',
    priceTrend: 'down',
    percentOff: 44,
  },
];

export default function HotDeals() {
  const [wishlisted, setWishlisted] = useState<Set<string>>(new Set());

  const toggleWishlist = (id: string) => {
    setWishlisted((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section className="py-16 px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="FireIcon" size={20} className="text-warning" />
            <h2 className="text-2xl font-700 text-foreground">Hot Deals Right Now</h2>
          </div>
          <p className="text-muted-foreground text-sm">
            Hand-picked deals — prices valid for next 48 hours
          </p>
        </div>
        <Link
          href="/search-results-page"
          className="text-primary text-sm font-semibold hover:underline flex items-center gap-1"
        >
          See all deals
          <Icon name="ArrowRightIcon" size={14} />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        {deals.map((deal) => (
          <div
            key={deal.id}
            className="relative bg-card border border-border rounded-2xl p-4 deal-card-hover"
          >
            {/* Wishlist */}
            <button
              type="button"
              onClick={() => toggleWishlist(deal.id)}
              aria-label={wishlisted.has(deal.id) ? 'Remove from wishlist' : 'Add to wishlist'}
              className={`absolute top-3 right-3 wishlist-btn p-1.5 rounded-full transition-colors ${
                wishlisted.has(deal.id)
                  ? 'text-negative bg-negative-bg' :'text-muted-foreground hover:text-negative'
              }`}
            >
              <Icon
                name={wishlisted.has(deal.id) ? 'HeartIcon' : 'HeartIcon'}
                variant={wishlisted.has(deal.id) ? 'solid' : 'outline'}
                size={18}
              />
            </button>

            {/* Badge */}
            <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-3 ${deal.badgeColor}`}>
              {deal.badge}
            </span>

            {/* Deal Type Icon */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                <Icon
                  name={
                    deal.type === 'flight' ?'PaperAirplaneIcon'
                      : deal.type === 'hotel' ?'BuildingOffice2Icon' :'GiftIcon'
                  }
                  size={18}
                  className="text-primary"
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground capitalize font-medium">{deal.type}</p>
                <p className="text-sm font-700 text-foreground leading-tight">
                  {deal.type === 'hotel' ? deal.destination : `${deal.origin?.split(' (')[0]} → ${deal.destination.split(' (')[0]}`}
                </p>
              </div>
            </div>

            {/* Details */}
            {deal.type === 'flight' && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1">
                  <Icon name="ClockIcon" size={12} />
                  {deal.duration}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="ArrowPathIcon" size={12} />
                  {deal.stops}
                </span>
                <span>{deal.departure}</span>
              </div>
            )}
            {deal.type === 'hotel' && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1">
                  {'★'.repeat(deal.stars ?? 0)}
                  <span className="ml-0.5">{deal.stars}-star</span>
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="StarIcon" size={12} className="text-warning" />
                  {deal.rating} ({deal.reviewCount?.toLocaleString()})
                </span>
              </div>
            )}
            {deal.type === 'package' && (
              <div className="text-xs text-muted-foreground mb-3">
                <p>{deal.hotel} · {deal.nights} nights · {deal.departure}</p>
              </div>
            )}

            {/* Price */}
            <div className="flex items-end justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-800 text-foreground tabular-nums">
                    ${deal.price}
                  </span>
                  {deal.type === 'hotel' && (
                    <span className="text-xs text-muted-foreground">/night</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-muted-foreground line-through tabular-nums">
                    ${deal.originalPrice}
                  </span>
                  <span className="text-xs font-semibold text-positive">
                    Save ${deal.savings} ({deal.percentOff}% off)
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs price-trend-down font-medium">
                <Icon name="ArrowTrendingDownIcon" size={14} />
                <span>Price drop</span>
              </div>
            </div>

            {/* CTA */}
            <Link
              href="/search-results-page"
              className="mt-3 block w-full text-center btn-primary py-2.5 text-sm"
            >
              View Deal
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}